import { HttpException, HttpStatus, Injectable } from '@nestjs/common';
import { HuaweiOidDB } from './huawei_oid.service';
import { OidDB } from './oid.service';
import { SnmpService } from './snmp.service';
import { Slot, Oid, Vlans, VlanType } from '@ip6m/types';

@Injectable()
export class OltService {
  
    constructor(
        private oidDB: OidDB, 
        private huawei_oidDB: HuaweiOidDB,
        private snmpService: SnmpService) {};

    async getSysinfo(oltid: number) {
        const systemOid = this.oidDB.System();
        const oids = this.oidDB.getChildrenOids(systemOid);
        const names = this.oidDB.getChildrenNames(systemOid);
        try {
            const system:Oid[] = await this.snmpService.get(oltid, oids);
            for(let i=0; i<system.length; i++) {
                system[i].name = names[i];
            }
            return system;
        } catch (error) {
            throw new HttpException(error.message, HttpStatus.REQUEST_TIMEOUT);
        }
    };

    async getFrameInfo(oltid: number) {
        const frameOid = this.huawei_oidDB.Frame();
        const names = this.huawei_oidDB.getChildrenNames(frameOid);

        try {
            const frame: Oid[] = await this.snmpService.subtree(oltid, frameOid.base);
            for(let i=0; i<frame.length; i++) {
                frame[i].name = names[i];
            }
            return frame;
        } catch(error) {
            throw new HttpException(error.message, HttpStatus.REQUEST_TIMEOUT);  
        }
    }
    
    async getSlots(oltid: number) {
        const slotsOid = this.huawei_oidDB.Slots();
        const subslotsOid = this.huawei_oidDB.SubSlots();
        const oids = this.oidDB.getChildrenOids(slotsOid);
        const oidnames = this.oidDB.getChildrenNames(slotsOid);
        const suboids = this.oidDB.getChildrenOids(subslotsOid);
        const suboidnames = this.oidDB.getChildrenNames(subslotsOid);

        const slotIndexSnmp = await this.snmpService.subtree(oltid, oids[0]);
        const subslotIndexSnmp = await this.snmpService.subtree(oltid, suboids[0]);

        const subSlots: Object = {};
        for(let i=0; i<subslotIndexSnmp.length; i++) {
            const split = subslotIndexSnmp[i].oid.split('.');
            const subslotIndex = split.pop(); // last value
            const slotIndex = split.pop();    // value before last value
            if(subSlots[slotIndex]) {
                subSlots[slotIndex].push(subslotIndex);
            } else {
                subSlots[slotIndex] = [subslotIndex];
            }
        }
        
        // Now we have the slots with subslots in it in subSlots object. 
        // We have to request subslot oids too for these slots.

        const genSlotOids: string[] = [];
        const genSubslotOids: string[] = [];
        const result: Slot[] = [];

        // generate slot oid list to be requested to every slot
        oids.forEach((oid) => {
            for(let i=0; i<slotIndexSnmp.length; i++) {
                genSlotOids.push(oid + "." + slotIndexSnmp[i].value);
            }
        })
        // add subslot oids too
        suboids.forEach((oid) => {
            for(let slotid in subSlots) {
                if (subSlots.hasOwnProperty(slotid)) {
                    subSlots[slotid].forEach((subslotId) =>  {
                        genSubslotOids.push(oid + "." + slotid + "." + subslotId);
                    })
                }
            }
        })

        // Initialize result with slotindexes
        slotIndexSnmp.forEach((index) => {
            result.push({'hwSlotIndex': index.value });
        });

        
        // Add slot data to result
        let snmpResponse = await this.snmpService.get(oltid, genSlotOids)
        for(let i=0; i<snmpResponse.length; i++) {
            const slot = i % slotIndexSnmp.length;
            const oidnum = Math.trunc(i / slotIndexSnmp.length);
               result[slot][oidnames[oidnum]] = snmpResponse[i].value;
        };
        
        // Add subslot data to result where applicable
        snmpResponse = await this.snmpService.get(oltid, genSubslotOids) as Oid[];
        for(let i=0; i<snmpResponse.length; i++) {        
            const split = snmpResponse[i].oid.split('.');
            split.pop();  // remove subslotindex
            const slotIndex = split.pop();
            result.forEach((record) => {
                if(record.hwSlotIndex == slotIndex) {
                    const oidnum = Math.trunc(i / subslotIndexSnmp.length);
                    if(suboidnames[oidnum] == 'hwSubslotPorts') {
                        if(typeof record['portNum'] === 'undefined') {
                            record['portNum'] = snmpResponse[i].value;
                        }
                        else {
                            record['portNum'] += snmpResponse[i].value;
                        }
                    }
                }
            })
        };
        return result;
    }

    async getVlans(oltid: number) {
        const vlansOid = this.huawei_oidDB.Vlans();
        const oids = this.huawei_oidDB.getChildrenOids(vlansOid);
        const names = this.huawei_oidDB.getChildrenNames(vlansOid);
        let vlans: Vlans = {}

        try {
            const snmpVlanIndex: Oid[] = await this.snmpService.subtree(oltid, oids[0]);
            for(let i=0; i<snmpVlanIndex.length; i++) {
               const vlanId = snmpVlanIndex[i].value;

               // Get descr, ports, vlanType as well:
               let otherOids: string[] = [];
               for(let j = 1; j < oids.length; j++) {
                   otherOids.push(oids[j] + "." + vlanId);
               }
               const snmpOtherOids: Oid[] = await this.snmpService.get(oltid, otherOids);

               vlans[vlanId] = {
                   id: vlanId,
                   descr: snmpOtherOids[0].value.toString(),
                   ports: snmpOtherOids[1].value.toString(),
                   type: VlanType[snmpOtherOids[2].value]
               };
            }
            return vlans;
        } catch(error) {
            throw new HttpException(error.message, HttpStatus.BAD_REQUEST);  
        }
    }
     
}
